import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  RefreshCw, Maximize2, ZoomIn, ZoomOut, Server, Database, Network,
  Key, Globe, Shield, Cloud, Zap, Lock, AlertTriangle, Box, Info,
} from 'lucide-react';
import { apiClient } from '../../services/api/client';


// ─── Types ───────────────────────────────────────────────────────────────────
//
// The graph-engine builder (_build_pg_wiz_graph) ships nodes keyed by
// `wizLayer` (INTERNET / RESOURCES / IDENTITY / TARGETS / FINDINGS) and does
// NOT emit x/y — the FE is expected to lay them out, exactly like
// SecurityGraph.tsx does. This panel therefore computes its own columnar
// attack-path layout from wizLayer, focuses the view on a center node, and
// caps the neighborhood so a large tenant graph doesn't render as a hairball.

interface RawNode {
  id: string;
  name?: string;
  type?: string;
  provider?: string;
  region?: string;
  riskLevel?: string;
  wizLayer?: string;
  layer?: number;
  isCrownJewel?: boolean;
  isPubliclyExposed?: boolean;
  hasVulnerabilities?: boolean;
  hasFinding?: boolean;
  findingCount?: number;
  finding_count?: number;
  isCenter?: boolean;
  details?: Record<string, any>;
  x?: number;
  y?: number;
}

interface RawEdge {
  id?: string;
  source: string;
  target: string;
  type?: string;
  label?: string;
  riskLevel?: string;
  onAttackPath?: boolean;
}

interface GraphData {
  finding?: Record<string, any>;
  resource?: Record<string, any>;
  nodes: RawNode[];
  edges: RawEdge[];
  source: string;
}

// Node after normalisation + client-side layout (fractional x/y in 0..1).
interface LaidOutNode {
  id: string;
  name: string;
  type: string;
  provider: string;
  region: string;
  riskLevel: string;
  isCrownJewel: boolean;
  isPubliclyExposed: boolean;
  hasFinding: boolean;
  isCenter: boolean;
  details: Record<string, any>;
  x: number;
  y: number;
}

interface RenderEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  label: string;
  riskLevel: string;
  onAttackPath: boolean;
}

interface ProcessedGraph {
  nodes: LaidOutNode[];
  edges: RenderEdge[];
  source: string;
  total: number;   // total nodes returned by the backend
  shown: number;   // nodes actually rendered after focusing/capping
  centerRequested: boolean;  // caller / payload named a specific center
  centerResolved: boolean;   // that center was actually found as a node
}

interface Props {
  /** Drive the graph from a unified-store finding id. */
  findingId?: string;
  /** Drive the graph from a cloud resource id / ARN (any pillar). */
  resourceId?: string;
  /** Explicit id/name to treat as the center of the neighborhood. */
  centerId?: string;
  resourceName?: string;
  /** Canvas height in px (default 320). */
  height?: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const NODE_R = 22;

// Max nodes to render. A tenant graph can be 300-500 nodes; showing all of
// them in a drawer-sized canvas is unreadable. We focus on the center's
// closest, highest-value neighborhood.
const MAX_NODES = 46;

// Wiz-parity 5-column attack-path flow: internet → resources → identity →
// targets (data/secrets) → findings. Fractional x per column.
const LAYER_X: Record<string, number> = {
  INTERNET: 0.09,
  RESOURCES: 0.33,
  IDENTITY: 0.55,
  TARGETS: 0.77,
  FINDINGS: 0.93,
};
const COLUMN_ORDER = ['INTERNET', 'RESOURCES', 'IDENTITY', 'TARGETS', 'FINDINGS'];

// Fallback: map the numeric `layer` the builder also emits.
const LAYER_NUM_TO_WIZ: Record<number, string> = {
  0: 'INTERNET', 1: 'RESOURCES', 2: 'IDENTITY', 3: 'TARGETS', 4: 'FINDINGS',
};

const RISK_W: Record<string, number> = {
  critical: 4, high: 3, medium: 2, low: 1, none: 0, info: 0,
};

const RISK_COLOR: Record<string, string> = {
  critical: '#EF4444',
  high: '#F97316',
  medium: '#EAB308',
  low: '#22C55E',
  none: '#6B7280',
  info: '#6B7280',
};

const TYPE_COLOR: Record<string, string> = {
  compute: '#3B82F6',
  storage: '#10B981',
  database: '#06B6D4',
  network: '#8B5CF6',
  identity: '#EC4899',
  secret: '#DC2626',
  internet: '#EF4444',
  function: '#F59E0B',
  container: '#14B8A6',
  kubernetes: '#6366F1',
  monitoring: '#64748B',
  data: '#0891B2',
};

const NODE_ICON: Record<string, React.FC<any>> = {
  compute: Server,
  storage: Database,
  database: Database,
  network: Network,
  identity: Key,
  internet: Globe,
  secret: Lock,
  function: Zap,
  container: Box,
  kubernetes: Shield,
  monitoring: Info,
};

const EDGE_RISK_COLOR: Record<string, string> = {
  critical: '#EF4444',
  high: '#F97316',
  medium: '#EAB308',
  low: '#22C55E',
  none: 'var(--border-color)',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function nodeColor(node: LaidOutNode): string {
  if (node.isCenter) return RISK_COLOR[node.riskLevel] ?? RISK_COLOR.none;
  return TYPE_COLOR[node.type] ?? '#6B7280';
}

function clampPos(v: number, r: number, max: number): number {
  if (!Number.isFinite(v)) return max / 2;
  return Math.max(r + 4, Math.min(max - r - 4, v));
}

function columnFor(n: RawNode): string {
  const wl = (n.wizLayer || '').toUpperCase();
  if (wl in LAYER_X) return wl;
  if (typeof n.layer === 'number' && LAYER_NUM_TO_WIZ[n.layer]) return LAYER_NUM_TO_WIZ[n.layer];
  return 'RESOURCES';
}

function nodeHasFinding(n: RawNode): boolean {
  return Boolean(n.hasFinding || n.hasVulnerabilities
    || (n.findingCount ?? n.finding_count ?? 0) > 0);
}

function nodePriority(n: RawNode): number {
  return (n.isCrownJewel ? 4 : 0)
    + (n.isPubliclyExposed ? 3 : 0)
    + (nodeHasFinding(n) ? 2 : 0)
    + (RISK_W[(n.riskLevel || 'none').toLowerCase()] ?? 0);
}

// Turn the raw backend payload into a focused, laid-out graph. Pure function
// of (data, centerId) so it memoises cleanly and stays independent of the
// pixel dimensions (those are applied later).
function processGraph(data: GraphData, centerId?: string): ProcessedGraph {
  const rawNodes = Array.isArray(data.nodes) ? data.nodes : [];
  const rawEdges = Array.isArray(data.edges) ? data.edges : [];
  const total = rawNodes.length;

  // Resolve the center. Prefer explicit prop, then a server-flagged center,
  // then the finding's / resource's own id echoed back in the payload.
  const echoed = centerId
    || (data.finding && (data.finding.resource_id || data.finding.asset_id))
    || (data.resource && data.resource.id)
    || null;
  const byId = new Map(rawNodes.map(n => [n.id, n]));
  const centerRequested = Boolean(echoed);
  let centerResolved = false;
  let center: RawNode | null = rawNodes.find(n => n.isCenter) || null;
  if (center) {
    centerResolved = true;
  } else if (echoed) {
    center = byId.get(echoed) || rawNodes.find(n => n.name === echoed) || null;
    centerResolved = Boolean(center);
  }
  // Last resort: the highest-priority finding-bearing node (not a real match).
  if (!center && rawNodes.length) {
    center = [...rawNodes].sort((a, b) => nodePriority(b) - nodePriority(a))[0];
  }

  // Undirected adjacency for BFS-distance focusing.
  const adj = new Map<string, string[]>();
  for (const e of rawEdges) {
    if (!byId.has(e.source) || !byId.has(e.target)) continue;
    (adj.get(e.source) || adj.set(e.source, []).get(e.source)!).push(e.target);
    (adj.get(e.target) || adj.set(e.target, []).get(e.target)!).push(e.source);
  }
  const dist = new Map<string, number>();
  if (center) {
    dist.set(center.id, 0);
    let frontier = [center.id];
    let d = 0;
    while (frontier.length && d < 6) {
      d += 1;
      const next: string[] = [];
      for (const id of frontier) {
        for (const nb of adj.get(id) || []) {
          if (!dist.has(nb)) { dist.set(nb, d); next.push(nb); }
        }
      }
      frontier = next;
    }
  }

  // Focus: keep the center + closest, highest-value nodes up to MAX_NODES.
  let kept = rawNodes;
  if (rawNodes.length > MAX_NODES) {
    kept = [...rawNodes].sort((a, b) => {
      const da = dist.has(a.id) ? dist.get(a.id)! : 99;
      const db = dist.has(b.id) ? dist.get(b.id)! : 99;
      if (da !== db) return da - db;               // closer to center first
      return nodePriority(b) - nodePriority(a);      // then most security-relevant
    }).slice(0, MAX_NODES);
    // Guarantee the center survives the slice.
    if (center && !kept.some(n => n.id === center!.id)) {
      kept = [center, ...kept.slice(0, MAX_NODES - 1)];
    }
  }
  const keptIds = new Set(kept.map(n => n.id));

  // ── Columnar layout ──────────────────────────────────────────────────────
  // Bucket by wizLayer column, then spread nodes vertically within each
  // column. The center is pinned to the vertical middle of its column.
  const columns = new Map<string, RawNode[]>();
  for (const n of kept) {
    const col = columnFor(n);
    (columns.get(col) || columns.set(col, []).get(col)!).push(n);
  }

  const laidOut: LaidOutNode[] = [];
  for (const col of COLUMN_ORDER) {
    const bucket = columns.get(col);
    if (!bucket || !bucket.length) continue;
    // Center pinned first, then by descending priority for a stable, readable
    // top-to-bottom order.
    bucket.sort((a, b) => {
      if (center && a.id === center.id) return -1;
      if (center && b.id === center.id) return 1;
      return nodePriority(b) - nodePriority(a);
    });
    const x = LAYER_X[col];
    const count = bucket.length;
    bucket.forEach((n, i) => {
      const isCenter = Boolean(center && n.id === center.id);
      // Even vertical spread in 0.12..0.88; single-node columns sit centered.
      const y = count === 1 ? 0.5 : 0.12 + (0.76 * i) / (count - 1);
      laidOut.push({
        id: n.id,
        name: n.name || n.id,
        type: (n.type || 'compute').toLowerCase(),
        provider: n.provider || '',
        region: n.region || (n.details && (n.details.region || n.details.location)) || '',
        riskLevel: (n.riskLevel || 'none').toLowerCase(),
        isCrownJewel: Boolean(n.isCrownJewel),
        isPubliclyExposed: Boolean(n.isPubliclyExposed),
        hasFinding: nodeHasFinding(n),
        isCenter,
        details: n.details || {},
        x,
        y: isCenter ? 0.5 : y,
      });
    });
  }

  // Edges limited to the kept node set; derive a stable id + risk colour.
  const edges: RenderEdge[] = [];
  rawEdges.forEach((e, i) => {
    if (!keptIds.has(e.source) || !keptIds.has(e.target)) return;
    const onPath = Boolean(e.onAttackPath);
    edges.push({
      id: e.id || `${e.source}->${e.target}-${i}`,
      source: e.source,
      target: e.target,
      type: e.type || 'RELATED_TO',
      label: e.label || '',
      riskLevel: onPath ? 'critical' : (e.riskLevel || 'none').toLowerCase(),
      onAttackPath: onPath,
    });
  });

  return {
    nodes: laidOut, edges, source: data.source, total, shown: laidOut.length,
    centerRequested, centerResolved,
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

const FindingGraphPanel: React.FC<Props> = ({ findingId, resourceId, centerId, resourceName, height = 320 }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<LaidOutNode | null>(null);
  const [selectedNode, setSelectedNode] = useState<LaidOutNode | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dims, setDims] = useState({ w: 600, h: height });


  // ── Measure container ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      const { width, height: h } = entries[0].contentRect;
      setDims({ w: Math.max(300, width), h: Math.max(220, h) });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // ── Fetch graph data ───────────────────────────────────────────────────────
  // Two drivers: a unified-store finding id (→ /findings/{id}/graph, which
  // resolves the finding's asset and centers on it) or a raw resource id
  // (→ /wiz-graph?resource_id=, the same builder centered on the resource).
  const fetchGraph = useCallback(async () => {
    if (!findingId && !resourceId) {
      setError('No finding or resource to graph');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = findingId
        ? `/api/v1/findings/${encodeURIComponent(findingId)}/graph`
        : `/api/v1/wiz-graph?resource_id=${encodeURIComponent(resourceId!)}`;
      const { data: gdata, error: err, status } = await apiClient.get<GraphData>(url);
      if (err || !gdata) throw new Error(`HTTP ${status}`);
      setData(gdata);
    } catch (e: any) {
      setError(e.message || 'Failed to load graph');
    } finally {
      setLoading(false);
    }
  }, [findingId, resourceId]);

  useEffect(() => { fetchGraph(); }, [fetchGraph]);

  // ── Focus + lay out (pure of pixel dims) ─────────────────────────────────────
  const processed = React.useMemo(
    () => (data ? processGraph(data, centerId || resourceId) : null),
    [data, centerId, resourceId],
  );

  // ── Compute pixel positions ────────────────────────────────────────────────
  const positionedNodes = React.useMemo(() => {
    if (!processed) return [];
    return processed.nodes.map(n => ({
      ...n,
      px: clampPos(n.x * dims.w, NODE_R, dims.w),
      py: clampPos(n.y * dims.h, NODE_R, dims.h),
    }));
  }, [processed, dims]);

  const nodeMap = React.useMemo(() => {
    const m: Record<string, typeof positionedNodes[0]> = {};
    positionedNodes.forEach(n => { m[n.id] = n; });
    return m;
  }, [positionedNodes]);

  // ── Pan & zoom ─────────────────────────────────────────────────────────────
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if ((e.target as SVGElement).closest('.graph-node')) return;
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setDragging(false);
  const handleWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    setZoom(z => Math.max(0.3, Math.min(3, z - e.deltaY * 0.001)));
  };

  const zoomIn = () => setZoom(z => Math.min(3, z + 0.2));
  const zoomOut = () => setZoom(z => Math.max(0.3, z - 0.2));
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  if (loading) {
    return (
      <div className="flex items-center justify-center text-gray-500" style={{ height }}>
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-teal-500" />
        Loading relationship graph…
      </div>
    );
  }

  // Honest guard against the backend's global-graph fallback: when a specific
  // center was requested but couldn't be resolved to a node AND the payload is
  // large, the builder returned the whole-tenant graph rather than this
  // resource's neighborhood. Rendering that would misleadingly imply the
  // resource connects to hundreds of assets — show an honest empty state.
  const globalFallback = Boolean(
    processed && processed.centerRequested && !processed.centerResolved
    && processed.total > MAX_NODES,
  );

  if (error || !processed || processed.nodes.length === 0 || globalFallback) {
    return (
      <div className="flex items-center justify-center text-gray-600 gap-2" style={{ height }}>
        <AlertTriangle className="w-4 h-4 text-yellow-600" />
        <span className="text-sm">
          {error
            ? `Graph unavailable — ${error}`
            : globalFallback
              ? 'This resource is not in the security graph yet — run a scan to map its relationships.'
              : 'No graph relationships for this resource yet.'}
        </span>
      </div>
    );
  }

  const transformStr = `translate(${pan.x}, ${pan.y}) scale(${zoom})`;
  const attackPathEdges = processed.edges.filter(e => e.onAttackPath).length;

  return (
    <div className="flex flex-col gap-2">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
          <span className={`w-2 h-2 rounded-full ${processed.source === 'neo4j' ? 'bg-green-500' : 'bg-blue-500'}`} />
          {processed.source === 'neo4j' ? 'Live Neo4j graph' : 'Live graph (PostgreSQL)'}
          <span className="text-gray-600">·</span>
          <span>
            {processed.shown < processed.total
              ? `${processed.shown} of ${processed.total} nodes`
              : `${processed.shown} nodes`} · {processed.edges.length} edges
          </span>
          {attackPathEdges > 0 && (
            <>
              <span className="text-gray-600">·</span>
              <span className="text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />{attackPathEdges} on attack path
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={zoomIn}
            className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-white/5">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <button onClick={zoomOut}
            className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-white/5">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <button onClick={resetView}
            className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-white/5">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={fetchGraph}
            className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-white/5">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* SVG graph */}
      <div
        ref={containerRef}
        className="relative bg-wiz-darker rounded-lg border border-gray-800 overflow-hidden"
        style={{ height }}
      >
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ cursor: dragging ? 'grabbing' : 'grab' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          <defs>
            {/* Grid pattern */}
            <pattern id="graph-grid" width="30" height="30" patternUnits="userSpaceOnUse">
              <path d="M 30 0 L 0 0 0 30" fill="none" stroke="var(--bg-elevated)" strokeWidth="0.5" />
            </pattern>
            {/* Arrow markers */}
            {Object.entries(EDGE_RISK_COLOR).map(([risk, color]) => (
              <marker
                key={risk}
                id={`arrow-${risk}`}
                markerWidth="8" markerHeight="8"
                refX="6" refY="3"
                orient="auto"
              >
                <path d="M0,0 L0,6 L8,3 z" fill={color} opacity="0.7" />
              </marker>
            ))}
            {/* Glow filter for center node */}
            <filter id="glow-center">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" />
              <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id="glow-finding">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* Background grid */}
          <rect width="100%" height="100%" fill="url(#graph-grid)" />

          <g transform={transformStr}>
            {/* ── Edges ── */}
            {processed.edges.map(edge => {
              const src = nodeMap[edge.source];
              const tgt = nodeMap[edge.target];
              if (!src || !tgt) return null;
              const edgeColor = edge.onAttackPath
                ? '#EF4444'
                : EDGE_RISK_COLOR[edge.riskLevel] ?? EDGE_RISK_COLOR.none;
              const mx = (src.px + tgt.px) / 2;
              const my = (src.py + tgt.py) / 2;
              const markerId = `arrow-${edge.riskLevel in EDGE_RISK_COLOR ? edge.riskLevel : 'none'}`;
              return (
                <g key={edge.id}>
                  <line
                    x1={src.px} y1={src.py}
                    x2={tgt.px} y2={tgt.py}
                    stroke={edgeColor}
                    strokeWidth={edge.onAttackPath ? 2.25 : 1.5}
                    strokeOpacity={edge.onAttackPath ? 0.85 : 0.45}
                    strokeDasharray={edge.type === 'RELATED_TO' ? '4 3' : undefined}
                    markerEnd={`url(#${markerId})`}
                  />
                  {edge.label && (
                    <text
                      x={mx} y={my - 4}
                      textAnchor="middle"
                      fontSize="8"
                      fill={edgeColor}
                      fillOpacity={0.7}
                      style={{ userSelect: 'none', pointerEvents: 'none' }}
                    >
                      {edge.label}
                    </text>
                  )}
                </g>
              );
            })}

            {/* ── Nodes ── */}
            {positionedNodes.map(node => {
              const color = nodeColor(node);
              const isHovered = hoveredNode?.id === node.id;
              const isSelected = selectedNode?.id === node.id;
              const r = node.isCenter ? NODE_R + 6 : NODE_R;
              const Icon = NODE_ICON[node.type] ?? Cloud;

              return (
                <g
                  key={node.id}
                  className="graph-node"
                  transform={`translate(${node.px}, ${node.py})`}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredNode(node)}
                  onMouseLeave={() => setHoveredNode(null)}
                  onClick={() => setSelectedNode(selectedNode?.id === node.id ? null : node)}
                >
                  {/* Outer ring for center/finding nodes */}
                  {(node.isCenter || node.hasFinding) && (
                    <circle
                      r={r + 5}
                      fill="none"
                      stroke={color}
                      strokeWidth={node.isCenter ? 2 : 1}
                      strokeOpacity={0.3}
                      strokeDasharray={node.isCenter ? undefined : '3 2'}
                    />
                  )}

                  {/* Selection ring */}
                  {(isHovered || isSelected) && (
                    <circle r={r + 8} fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.6} />
                  )}

                  {/* Main circle */}
                  <circle
                    r={r}
                    fill={color}
                    fillOpacity={node.isCenter ? 0.25 : 0.15}
                    stroke={color}
                    strokeWidth={node.isCenter ? 2.5 : 1.5}
                    filter={node.isCenter ? 'url(#glow-center)' : node.hasFinding ? 'url(#glow-finding)' : undefined}
                  />

                  {/* Crown jewel indicator */}
                  {node.isCrownJewel && (
                    <text x={r - 6} y={-r + 6} fontSize="10" textAnchor="middle" style={{ pointerEvents: 'none' }}>
                      👑
                    </text>
                  )}

                  {/* Internet exposed indicator */}
                  {node.isPubliclyExposed && (
                    <circle cx={r - 4} cy={-r + 4} r={5} fill="#EF4444" stroke="#0a0e1a" strokeWidth={1.5} />
                  )}

                  {/* Icon (rendered as SVG text fallback — Lucide needs foreignObject) */}
                  <foreignObject x={-10} y={-10} width={20} height={20} style={{ pointerEvents: 'none' }}>
                    <div
                      style={{
                        width: 20, height: 20, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        color: color,
                      }}
                    >
                      <Icon style={{ width: 14, height: 14 }} />
                    </div>
                  </foreignObject>

                  {/* Label */}
                  <text
                    y={r + 14}
                    textAnchor="middle"
                    fontSize="9"
                    fill="var(--text-tertiary)"
                    style={{ userSelect: 'none', pointerEvents: 'none' }}
                  >
                    {node.name.length > 18 ? node.name.slice(0, 16) + '…' : node.name}
                  </text>

                  {/* Type label */}
                  <text
                    y={r + 24}
                    textAnchor="middle"
                    fontSize="8"
                    fill="#4B5563"
                    style={{ userSelect: 'none', pointerEvents: 'none' }}
                  >
                    {node.type}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>

        {/* Hover tooltip */}
        {hoveredNode && (
          <div
            className="absolute bottom-3 left-3 bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs max-w-[220px] pointer-events-none z-10 shadow-xl"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: nodeColor(hoveredNode) }} />
              <span className="font-semibold text-white truncate">{hoveredNode.name}</span>
            </div>
            <div className="space-y-1 text-gray-400">
              <div className="flex items-center justify-between gap-2">
                <span>Type</span>
                <span className="text-gray-300">{hoveredNode.type}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>Risk</span>
                <span style={{ color: RISK_COLOR[hoveredNode.riskLevel] ?? 'var(--text-tertiary)' }}>
                  {hoveredNode.riskLevel}
                </span>
              </div>
              {hoveredNode.provider && (
                <div className="flex items-center justify-between gap-2">
                  <span>Provider</span>
                  <span className="text-gray-300 uppercase">{hoveredNode.provider}</span>
                </div>
              )}
              {hoveredNode.region && (
                <div className="flex items-center justify-between gap-2">
                  <span>Region</span>
                  <span className="text-gray-300">{hoveredNode.region}</span>
                </div>
              )}
              {hoveredNode.hasFinding && (
                <div className="mt-1 text-orange-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />Has findings
                </div>
              )}
              {hoveredNode.isCrownJewel && (
                <div className="text-yellow-400">Crown Jewel asset</div>
              )}
              {hoveredNode.isPubliclyExposed && (
                <div className="text-red-400 flex items-center gap-1">
                  <Globe className="w-3 h-3" />Publicly exposed
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected node detail */}
      {selectedNode && (
        <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-3 text-xs">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: nodeColor(selectedNode) }} />
              <span className="font-semibold text-white">{selectedNode.name}</span>
              <span className="text-gray-600">({selectedNode.type})</span>
            </div>
            <button onClick={() => setSelectedNode(null)} className="text-gray-600 hover:text-gray-400">×</button>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-400">
            <span>Provider</span><span className="text-gray-200 uppercase">{selectedNode.provider || '—'}</span>
            <span>Region</span><span className="text-gray-200">{selectedNode.region || '—'}</span>
            <span>Risk</span>
            <span style={{ color: RISK_COLOR[selectedNode.riskLevel] ?? 'var(--text-tertiary)' }}>
              {selectedNode.riskLevel}
            </span>
            {selectedNode.details?.resourceType && (
              <><span>Resource type</span><span className="text-gray-200">{selectedNode.details.resourceType}</span></>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 pt-1 border-t border-gray-800">
        <span className="text-gray-600 font-medium">Flow:</span>
        <span>internet → resources → identity → data → findings</span>
        <span className="ml-auto flex items-center gap-2">
          <span className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-red-500" />attack path
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500" />exposed
          </span>
          <span className="flex items-center gap-1">
            <span className="text-yellow-500">👑</span>crown jewel
          </span>
        </span>
      </div>
    </div>
  );
};

export default FindingGraphPanel;
